import React, { useState, useEffect } from "react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import { Calendar, Edit, Trash2, Users, Grid2X2, CheckCircle, XCircle } from "lucide-react";
import LocalDateTime from "services/date-converter";
import Pagination from "modules/swiftboard/components/Pagination";
import sortData from "utils/sorting-coloum";
import { useAlert } from "context/AlertContext";
import apiUserRole from "./api";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import NoDataState from "modules/swiftboard/components/NoDataState";
import { roleConfig } from "../role-config";
import RoleModuleAccessModal from "../role-module-mapping/RoleModuleAccessModal";

const UserRole = ({ onTotalCountChange }) => {
    const { showAlert } = useAlert();
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [resetTrigger, setResetTrigger] = useState(0);
    const [accessRole, setAccessRole] = useState(null);
    const [confirmData, setConfirmData] = useState({
        isOpen: false,
        title: "",
        message: "",
        confirmText: "Yes",
        cancelText: "No",
        icon: null,
        resolve: null,
    });
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "asc",
    });

    const fetchUserRole = async () => {
        setLoading(true);
        try {
            const payload = {
                searchText: searchText,
                skip: (currentPage - 1) * pageSize,
                limit: pageSize,
            };


            const response = await apiUserRole.getAllUserRole(payload);

            if (response.data.status) {
                setData(response.data.data);
                setTotalCount(response.data.total_count || 0);
                onTotalCountChange?.(response.data.total_count || 0);
            }
            else {
                showAlert({ type: 'warning', message: response.data?.message });
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
            if (error.response) {
                showAlert({ type: 'error', message: error.response?.data?.message });
            } else {
                showAlert({ type: 'error', message: "An unexpected error occurred. Please try again!" });
            }
        }
        finally {
            setLoading(false);
        }
    }

    const handleDeleteSelected = async (ids = []) => {
        if (ids.length === 0) {
            showAlert({ type: "warning", message: "Please select at least one role to delete." });
            return;
        }

        const confirmed = await openConfirm({
            title: "Delete Role",
            message:
                ids.length > 1
                    ? `Are you sure you want to delete ${ids.length} selected roles? This action cannot be undone.`
                    : "Are you sure you want to delete this role? This action cannot be undone.",
            confirmText: "Delete",
            cancelText: "Cancel",
            icon: <Trash2 size={24} className="text-danger" />,
        });

        if (!confirmed) return;

        try {
            const response = await apiUserRole.delete(ids);

            if (response.data.status) {
                showAlert({
                    type: "success",
                    message: response.data?.message,
                });

                fetchUserRole();
                setSelectedRows([]);
            } else {
                showAlert({
                    type: "warning",
                    message: response.data?.message,
                });
            }
        } catch (error) {
            showAlert({
                type: "error",
                message:
                    error.response?.data?.message ||
                    "An unexpected error occurred. Please try again!",
            });
        }
    };

    useEffect(() => {
        fetchUserRole();
    }, [currentPage, pageSize, searchText]);

    useEffect(() => {
        const sorted = sortData(data, sortConfig);
        setFilteredData(sorted);
    }, [data, sortConfig]);


    const handleSearch = (value) => {
        setSearchText(value)
        setCurrentPage(1); // reset to first page
    };

    // Handle page change
    const handlePageChange = (page, newPageSize = pageSize) => {
        setCurrentPage(page);
        if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
            setCurrentPage(1);
        }
    };



    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const handleCreate = () => {
        console.log("Open create user role modal");
        showAlert({ type: 'success', message: 'create user role' })
    };


    const handleRefresh = () => {
        setSearchText('');// to reset search text  
        setCurrentPage(1);// to reset page number to 1
        setResetTrigger(prev => prev + 1);// to reset search in the search bar 
        fetchUserRole();
    };

    const columns = [
        {
            key: "role",
            label: "Role Name",
            width: "220px",
            sortable: true,
            render: (row) => {
                const config = roleConfig[row.role] || roleConfig.EMPLOYEE;
                const Icon = config.icon;

                return (
                    <div
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${config.className}`}
                    >
                        <Icon size={14} />
                        {config.label}
                    </div>
                );
            },
        },

        {
            key: "_count",
            label: "User Count",
            width: "220px",
            render: (row) => {
                const count = row?._count?.user ?? 0;

                return (
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-surface_alt text-text_primary border text-xs font-semibold">
                        <Users size={14} className="text-text_secondary" />
                        <span>{count.toLocaleString()}</span>
                        <span className="text-[10px] text-text_muted font-medium">users</span>
                    </div>
                );
            },
        },
        {
            key: "is_activated",
            label: "Status",
            width: "140px",
            render: (row) => (
                <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${row.is_activated
                        ? "bg-success_bg text-success border border-success_border"
                        : "bg-danger_bg text-danger border border-danger_border"
                        }`}
                >
                    {row.is_activated ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            key: "created_date",
            label: "Created Date",
            width: "140px",
            render: (row) => (
                <div className="flex items-center gap-1.5 text-xs text-text_primary">
                    <Calendar size={12} className="text-text_muted" />
                    <LocalDateTime utcTime={row.created_date} type="datetime" />
                </div>
            ),
            sortable: true,
        },
        {
            key: "updated_date",
            label: "Last Update",
            width: "140px",
            render: (row) => (
                <div className="flex items-center gap-1.5 text-xs text-text_primary">
                    <Calendar size={12} className="text-text_muted" />
                    <LocalDateTime utcTime={row.updated_date} type="datetime" />
                </div>
            ),
            sortable: true,
        },
    ];
    const openConfirm = (options) => {
        return new Promise((resolve) => {
            setConfirmData({
                ...options,
                isOpen: true,
                resolve,
            });
        });
    };

    const handleConfirmClose = (result) => {
        if (confirmData.resolve) {
            confirmData.resolve(result);
        }

        setConfirmData((prev) => ({
            ...prev,
            isOpen: false,
            resolve: null,
        }));
    };

    const handleToggleStatus = async (row) => {
        try {
            const isCurrentlyActive = row.is_activated;

            const confirm = await openConfirm({
                title: isCurrentlyActive ? "Deactivate Role" : "Activate Role",
                message: isCurrentlyActive
                    ? "After deactivating this role, all assigned users will not be able to login to the portal. Are you sure you want to deactivate this role?"
                    : "Are you sure you want to activate this role?",
                confirmText: isCurrentlyActive ? "Deactivate" : "Activate",
                cancelText: "Cancel",
                icon: isCurrentlyActive
                    ? <XCircle size={24} className="text-danger" />
                    : <CheckCircle size={24} className="text-success" />,
            });

            if (!confirm) return;

            const response = await apiUserRole.toggleStatus(row.id);

            if (response.data.status) {
                showAlert({
                    type: "success",
                    message: response.data?.message
                });
                fetchUserRole();
            } else {
                showAlert({ type: "warning", message: response.data?.message });
            }
        } catch (error) {
            showAlert({
                type: "error",
                message: error.response?.data?.message || "Something went wrong"
            });
        }
    };


const renderRowActions = (row) => (
  <div className="flex justify-end">
    <div className="flex items-center gap-1 px-2 py-1
                     border border-border
                     rounded-lg bg-surface">

      {/* Activate / Deactivate */}
      <button
        className={`p-1 rounded-md transition-colors
        ${row.is_activated
          ? "text-danger hover:bg-danger_bg"
          : "text-success hover:bg-success_bg"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handleToggleStatus(row);
        }}
        title={row.is_activated ? "Deactivate" : "Activate"}
      >
        {row.is_activated ? (
          <XCircle size={16} strokeWidth={2.2} />
        ) : (
          <CheckCircle size={16} strokeWidth={2.2} />
        )}
      </button>

      {/* Module Assign */}
      <button
        className="flex items-center gap-1 px-2 py-1
                   rounded-md text-primary
                   hover:bg-primary_tint
                   transition-colors text-xs font-medium"
        onClick={(e) => {
          e.stopPropagation();
          setAccessRole(row);
        }}
        title="Assign Modules"
      >
        <Grid2X2 size={14} />
        <span className="hidden sm:inline">Modules</span>
        <span className="min-w-[18px] rounded-full bg-primary_tint px-1.5 text-center text-[10px] font-semibold tabular-nums text-primary">
          {row?._count?.module_mappings ?? 0}
        </span>
      </button>

      {/* Delete */}
      <button
        className="p-1 rounded-md text-danger hover:bg-danger_bg transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteSelected([row.id]);
        }}
        title="Delete"
      >
        <Trash2 size={15} />
      </button>

    </div>
  </div>
);
    return (
        <div className="h-full flex flex-col overflow-hidden bg-surface_alt">
            <div className="flex flex-col h-full gap-4">
                <TableToolBar
                    onSearch={handleSearch}
                    onRefresh={handleRefresh}
                    onCreate={handleCreate}
                    onDeleteSelected={() => handleDeleteSelected(selectedRows)}
                    selectedCount={selectedRows.length}
                    resetSearchTrigger={resetTrigger}
                    canCreate={false}
                />


                {data?.length === 0 && !loading ? (<div className="flex-1 flex items-center justify-center border rounded-lg border-border bg-surface">
                    <NoDataState
                        icon="users"
                        title="No User Roles Found"
                        message="There is no data available for this section."
                        showRefresh={false}
                        showBack={false}
                        size="medium"
                    />
                </div>) : (<>
                    <div className="flex-1 overflow-hidden border rounded-lg border-border">
                        <TableGrid
                            data={filteredData}
                            totalCount={totalCount}
                            loading={loading}
                            currentPage={currentPage}
                            columns={columns}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            onSelectionChange={setSelectedRows}
                            pageSize={pageSize}
                            renderActions={renderRowActions}
                        />
                    </div>

                    <Pagination
                        totalCount={totalCount}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                    />
                    {confirmData.isOpen && (
                        <ConfirmDialogue
                            confirmData={confirmData}
                            handleConfirmClose={handleConfirmClose}
                        />
                    )}
                </>)}

                {accessRole && (
                    <RoleModuleAccessModal
                        isOpen
                        role={accessRole}
                        onClose={() => setAccessRole(null)}
                        onSaved={fetchUserRole}
                    />
                )}


            </div>
        </div>
    );
};

export default UserRole;